import type { TaskStatus } from './types'

export type DeadlineState = 'on-time' | 'attention' | 'overdue' | 'none'

const DAY_IN_MS = 24 * 60 * 60 * 1000
const monthIndexes: Record<string, number> = {
  jan: 0, fev: 1, mar: 2, abr: 3, mai: 4, jun: 5,
  jul: 6, ago: 7, set: 8, out: 9, nov: 10, dez: 11,
}

function validDate(date: Date) {
  return Number.isFinite(date.getTime()) ? date : undefined
}

function atEndOfDay(date: Date) {
  date.setHours(23, 59, 0, 0)
  return date
}

export function parseTaskDeadline(value: string, now = new Date()) {
  const due = value.trim().toLocaleLowerCase('pt-BR')
  const todayMatch = due.match(/^hoje(?:,?\s*(\d{1,2}):(\d{2}))?$/)
  if (todayMatch) {
    const date = new Date(now)
    if (todayMatch[1]) date.setHours(Number(todayMatch[1]), Number(todayMatch[2]), 0, 0)
    else atEndOfDay(date)
    return date
  }

  if (due === 'amanhã' || due === 'amanha') {
    const date = new Date(now)
    date.setDate(date.getDate() + 1)
    return atEndOfDay(date)
  }

  const numericMatch = due.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (numericMatch) {
    return validDate(new Date(Number(numericMatch[3]), Number(numericMatch[2]) - 1, Number(numericMatch[1]), 23, 59))
  }

  const shortMatch = due.match(/^(\d{1,2})\s+([a-zç]{3})$/)
  if (shortMatch && monthIndexes[shortMatch[2]] !== undefined) {
    return validDate(new Date(now.getFullYear(), monthIndexes[shortMatch[2]], Number(shortMatch[1]), 23, 59))
  }

  return undefined
}

export function resolveDeadline(dueAt: string | undefined, taskDue: string, now = new Date()) {
  return dueAt ? validDate(new Date(dueAt)) : parseTaskDeadline(taskDue, now)
}

export function deadlineState(dueAt: string | undefined, taskDue: string, status: TaskStatus, now = new Date()): DeadlineState {
  if (status === 'Aprovada') return 'on-time'
  const deadline = resolveDeadline(dueAt, taskDue, now)
  if (!deadline) return 'none'
  const remaining = deadline.getTime() - now.getTime()
  if (remaining < 0) return 'overdue'
  if (remaining <= DAY_IN_MS) return 'attention'
  return 'on-time'
}

export function deadlineVisualState(state: DeadlineState, _ownerIsTiming: boolean): DeadlineState {
  return state
}

export function deadlineStateLabel(state: DeadlineState) {
  if (state === 'on-time') return 'No prazo'
  if (state === 'attention') return 'Vence em até 24h'
  if (state === 'overdue') return 'Prazo estourado'
  return 'Sem prazo'
}

export function toLocalDateTimeValue(date: Date | undefined) {
  if (!date) return ''
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}
